import React, { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import axios from "../axios/AxiosInstance";
import { apiErrorMessage } from "../utils/apiError";
import { authenticationFingerprint } from "../services/authSession";
import { candidateName } from "../utils/candidateName";
import { Search, RefreshCw, Eye, Link2, UserCheck, ShieldCheck, History, MessageSquare, CalendarCheck, Mail } from "lucide-react";
import { Tooltip, Modal } from "antd";
import "./CvDeliveryHistory.css";
const ClientDetail = lazy(() => import("../pages/ClientDetail"));
let candidateNameCache = { session: null, names: {}, at: 0 };
const decisions = {
  pending: "En attente",
  interview_requested: "Entretien demandé",
  accepted: "Accepté",
  declined: "Refusé",
};
const responses = {
  pending: "En attente",
  interested: "Intéressé",
  callback: "À rappeler",
  declined: "Refuse",
};
const channels = {
  phone: "Téléphone",
  email: "Email",
  sms: "SMS",
  other: "Autre",
  "client-space": "Espace client",
};
const date = (value) =>
  new Date(value).toLocaleString("fr-FR", { timeZone: "Europe/Paris" });
const presentationDay = value => {
  if (!value) return "";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "" : new Intl.DateTimeFormat("sv-SE", {timeZone:"Europe/Paris"}).format(parsed);
};
const statusTone = value => ["accepted", "interested"].includes(value) ? "positive" : value === "declined" ? "negative" : ["callback", "interview_requested"].includes(value) ? "active" : "pending";
export default function CvDeliveryHistory({
  kind,
  id,
  refresh = 0,
  orderId,
  clientView = false,
  compact = false,
}) {
  const [rows, setRows] = useState([]),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [loading, setLoading] = useState(true),
    [preview, setPreview] = useState(null),
    [edit, setEdit] = useState(null),
    [status, setStatus] = useState("pending"),
    [channel, setChannel] = useState("phone"),
    [comment, setComment] = useState(""),
    [busy, setBusy] = useState(false),
    [orders, setOrders] = useState([]),
    [selectedOrder, setSelectedOrder] = useState(""),
    [reviewers, setReviewers] = useState(null);
  const previous = useRef(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientLoading, setClientLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const clientRequest = useRef(null);
  useEffect(() => () => clientRequest.current?.abort(), []);
  const openClient = async row => {
    if (clientView || !row.clientId) return;
    clientRequest.current?.abort();
    const controller = new AbortController();
    clientRequest.current = controller;
    setClientLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`client/${encodeURIComponent(row.clientId)}`, { signal: controller.signal });
      if (controller.signal.aborted) return;
      if (!data?._id || String(data._id) !== String(row.clientId)) throw new Error("La fiche client est introuvable ou inaccessible.");
      setClients([data]);
      setSelectedClient(data);
    } catch (failure) {
      if (!controller.signal.aborted) setError(apiErrorMessage(failure, "Impossible de charger la fiche client."));
    } finally {
      if (!controller.signal.aborted) setClientLoading(false);
    }
  };
  const clientLabel = row => !clientView && row.clientId
    ? <button type="button" className="cvClientLink" disabled={clientLoading} aria-label={`Ouvrir la fiche de ${row.clientName || "ce client"}`} onClick={() => openClient(row)}>{row.clientName || "Client"}</button>
    : row.clientName || "Client";
  const [interviewFollowup, setInterviewFollowup] = useState(null);
  const [emailPreview, setEmailPreview] = useState(null);
  const [interviewDetails, setInterviewDetails] = useState({date:"",hour:"",address:""});
  const [previewReference, setPreviewReference] = useState("");
  const [candidateNames, setCandidateNames] = useState(() =>
    !clientView && candidateNameCache.session === authenticationFingerprint() && Date.now() - candidateNameCache.at < 300000 ? candidateNameCache.names : {});
  const [historyId, setHistoryId] = useState(null);
  const historyRow = rows.find(row => row._id === historyId);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState("");
  const [decisionFilter, setDecisionFilter] = useState("all");
  const [clientFilter, setClientFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sort, setSort] = useState("recent");
  const [groupBy, setGroupBy] = useState("client");
  const byCandidate = !clientView && groupBy === "candidate";
  const clientKey = row => String(row.clientId || row.clientName || "");
  const groupKey = row => byCandidate ? String(row.candidateId || row.reference || row._id) : clientKey(row);
  const groupName = row => byCandidate ? row.candidateName || candidateNames[row.candidateId] || row.reference || "Candidat non renseigné" : row.clientName || "Client non renseigné";
  const clientOptions = [...new Map(rows.map(row => [clientKey(row), row.clientName || "Client non renseigné"])).entries()].filter(([key]) => key).sort((a, b) => a[1].localeCompare(b[1], "fr"));
  const visibleRows = rows.filter(row => (!dateFrom || presentationDay(row.sentAt || row.createdAt) >= dateFrom) && (!dateTo || (presentationDay(row.sentAt || row.createdAt) && presentationDay(row.sentAt || row.createdAt) <= dateTo)) && (!clientFilter || clientKey(row) === clientFilter) && (decisionFilter === "all" || (row.clientDecision || "pending") === decisionFilter) && [row.reference, row.clientName, row.recipientEmail, row.senderName, !clientView && (row.candidateName || candidateNames[row.candidateId])].filter(Boolean).join(" ").toLocaleLowerCase("fr").includes(search.trim().toLocaleLowerCase("fr"))).sort((a, b) => {
    const byDate = (Date.parse(b.sentAt || b.createdAt) || 0) - (Date.parse(a.sentAt || a.createdAt) || 0);
    const byGroup = groupName(a).localeCompare(groupName(b), "fr") || groupKey(a).localeCompare(groupKey(b));
    return byGroup || (sort === "oldest" ? -byDate : byDate);
  });
  const pageCount = Math.max(1, Math.ceil(visibleRows.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pagedRows = visibleRows.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const candidateLookupKey = clientView ? "" : [...new Set(pagedRows.filter(row => row.candidateId && !row.candidateName).map(row => String(row.candidateId)))].sort().join(",");
  useEffect(() => {
    if (!candidateLookupKey) return;
    const controller = new AbortController();
    const session = authenticationFingerprint();
    const cached = candidateNameCache.session === session && Date.now() - candidateNameCache.at < 300000 ? candidateNameCache.names : {};
    const pending = candidateLookupKey.split(",").filter(id => !Object.prototype.hasOwnProperty.call(cached, id));
    let position = 0;
    const worker = async () => {
      while (position < pending.length && !controller.signal.aborted) {
        const candidateId = pending[position++];
        try {
          const { data } = await axios.get(`/tempworker-candidate/${encodeURIComponent(candidateId)}`, { signal: controller.signal });
          if (controller.signal.aborted || session !== authenticationFingerprint()) return;
          const name = data && String(data._id) === candidateId ? candidateName(data) : "";
          const previousNames = candidateNameCache.session === session ? candidateNameCache.names : {};
          candidateNameCache = { session, names: { ...previousNames, [candidateId]: name }, at: Date.now() };
          setCandidateNames(current => ({ ...current, [candidateId]: name }));
        } catch { /* La fiche reste accessible si le nom ne peut pas être chargé. */ }
      }
    };
    Promise.all(Array.from({ length: Math.min(4, pending.length) }, worker));
    return () => controller.abort();
  }, [candidateLookupKey]);
  const groups = new Map();
  visibleRows.forEach(row => {
    const key = groupKey(row);
    const group = groups.get(key) || { key, name: groupName(row), count: 0, pending: 0, clients: new Set(), rows: [] };
    group.count += 1;
    group.clients.add(clientKey(row));
    if (!row.clientDecision || row.clientDecision === "pending") group.pending += 1;
    groups.set(key, group);
  });
  pagedRows.forEach(row => groups.get(groupKey(row)).rows.push(row));
  const pagedGroups = [...groups.values()].filter(group => group.rows.length);
  useEffect(() => { setPage(1); }, [search, decisionFilter, clientFilter, dateFrom, dateTo, sort, groupBy, kind, id, orderId]);
  const load = useCallback(
    async (signal) => {
      try {
        const params = {
          ...(kind && id ? { [`${kind}Id`]: id } : {}),
          ...(orderId ? { orderId } : {}),
        };
        const { data } = await axios.get("cv-delivery/presentations", {
          params,
          signal,
        });
        const signature = JSON.stringify(
          data.map((row) => [row._id, row.revision, row.status]),
        );
        if (previous.current && previous.current !== signature)
          setNotice("Le suivi a été mis à jour.");
        previous.current = signature;
        setRows(data);
        setError("");
      } catch (e) {
        if (!signal?.aborted)
          setError(
            apiErrorMessage(
              e,
              "Impossible de charger le suivi des candidatures.",
            ),
          );
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [kind, id, orderId],
  );
  useEffect(() => {
    const controller = new AbortController();
    previous.current = null;
    setLoading(true);
    load(controller.signal);
    const timer = setInterval(() => {
      if (!document.hidden) load(controller.signal);
    }, 15000);
    const focus = () => load(controller.signal);
    window.addEventListener("focus", focus);
    window.addEventListener("jm-cv-delivery-updated", focus);
    return () => {
      controller.abort();
      clearInterval(timer);
      window.removeEventListener("focus", focus);
      window.removeEventListener("jm-cv-delivery-updated", focus);
    };
  }, [load, refresh]);
  useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview);
    },
    [preview],
  );
  const view = async (row) => {
    try {
      const { data } = await axios.get(`cv-delivery/${row._id}/file`);
      setPreviewReference(row.reference || "");
      setPreview(
        URL.createObjectURL(
          new Blob([Uint8Array.from(atob(data.data), (c) => c.charCodeAt(0))], {
            type: "application/pdf",
          }),
        ),
      );
    } catch (e) {
      setError(apiErrorMessage(e, "Impossible de charger le CV."));
    }
  };
  const begin = async (row, type) => {
    setError("");
    setComment("");
    setInterviewDetails(row.interviewDetails || {date:"",hour:"",address:""});
    setEdit({ row, type });
    setStatus(
      type === "client-decision"
        ? row.clientDecision || "pending"
        : row.candidateResponse || "pending",
    );
    setChannel("phone");
    setSelectedOrder(row.orderId || "");
    if (type === "order")
      try {
        const { data } = await axios.get(
          localStorage.getItem("userRole") === "FREELANCE"
            ? "order/freelance"
            : "order",
        );
        setOrders(
          data.filter(
            (o) => String(o.client?._id || o.client) === row.clientId,
          ),
        );
      } catch (e) {
        setError(apiErrorMessage(e, "Impossible de charger les commandes."));
      }
  };
  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const { data: savedRow } = await axios.patch(`cv-delivery/${edit.row._id}/${edit.type}`, {
        revision: edit.row.revision || 0,
        status,
        channel,
        comment,
        orderId: selectedOrder,
        ...(edit.type === "client-decision" && status === "interview_requested" ? {interviewDetails} : {}),
      });
      window.dispatchEvent(new Event("jm-cv-delivery-updated"));
      setEdit(null);
      await load();
      setNotice("Retour enregistré et partagé avec les espaces concernés.");
      if (edit.type === "client-decision" && status === "interview_requested" && !clientView) {
        await openInterviewFollowup(savedRow?._id ? savedRow : {...edit.row,interviewDetails,revision:(edit.row.revision || 0)+1});
        window.dispatchEvent(new Event("jm-task-updated"));
      }
    } catch (e) {
      setError(apiErrorMessage(e, "Impossible d’enregistrer ce retour."));
      if (e.response?.status === 409) {
        setEdit(null);
        await load();
        setError(
          "Le suivi a changé. Consultez la dernière version avant de répondre.",
        );
      }
    } finally {
      setBusy(false);
    }
  };
  const openInterviewFollowup = async row => {
    setInterviewFollowup({ row, loading:true });
    try {
      const {data} = await axios.post(`cv-delivery/${row._id}/interview-followup`);
      setInterviewFollowup({row,...data,loading:false});
    } catch (failure) {
      setInterviewFollowup({row,loading:false,error:apiErrorMessage(failure,"Le retour est enregistré, mais la création du suivi n’a pas pu être confirmée. Réessayez.")});
    }
  };
  const notifyInterview = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const {data} = await axios.post(`cv-delivery/${interviewFollowup.row._id}/interview-notification`,{confirmed:true});
      setInterviewFollowup(current => ({...current,error:"",notificationStatus:data.status}));
      await load();
    } catch (failure) {
      setInterviewFollowup(current => ({...current,error:apiErrorMessage(failure,"La notification n’a pas pu être envoyée.")}));
    } finally {setBusy(false);}
  };
  const manage = async (row) => {
    try {
      const { data } = await axios.get(`cv-delivery/reviewers/${row.clientId}`);
      setReviewers({ clientId: row.clientId, rows: data });
    } catch (e) {
      setError(apiErrorMessage(e, "Impossible de charger les habilitations."));
    }
  };
  const permission = async (user, enabled) => {
    setBusy(true);
    try {
      const { data } = await axios.patch(
        `cv-delivery/reviewers/${reviewers.clientId}/${user._id}`,
        { enabled },
      );
      setReviewers({ ...reviewers, rows: data });
    } catch (e) {
      setError(
        apiErrorMessage(e, "Impossible de modifier cette habilitation."),
      );
    } finally {
      setBusy(false);
    }
  };
  const staff =
    !clientView &&
    ["ADMIN", "SUPERADMIN", "HYPERADMIN", "MASTERADMIN", "AGENCY"].includes(
      localStorage.getItem("userRole"),
    );
  return (
    <section className="cvDeliveryHistory mt-4 rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        {!compact && <div>
          {clientView && <h3 className="text-lg font-semibold text-slate-900">Candidats proposés</h3>}
          <p className="m-0 text-sm text-slate-500">Retrouvez les profils présentés et suivez les réponses des clients.</p>
        </div>}
        <div className="ml-auto flex flex-wrap items-center gap-3">
          {!clientView && !compact && <div className="cvViewToggle" role="group" aria-label="Regrouper les CV proposés">
            <button type="button" aria-pressed={byCandidate} onClick={() => setGroupBy("candidate")}>Par candidat</button>
            <button type="button" aria-pressed={!byCandidate} onClick={() => setGroupBy("client")}>Par client</button>
          </div>}
        <button
          type="button"
          onClick={() => load()}
          className="cvRefresh"
        >
          <RefreshCw size={15} /> Actualiser
        </button>
        </div>
      </div>
      {!compact && <><div className="cvDeliveryToolbar">
        {!clientView && <select aria-label="Filtrer les CV par client" value={clientFilter} onChange={event => setClientFilter(event.target.value)}><option value="">Tous les clients</option>{clientOptions.map(([key, name]) => <option key={key} value={key}>{name}</option>)}</select>}
        <label className="cvDeliverySearch"><Search size={18} aria-hidden="true" /><input aria-label="Rechercher un CV proposé" placeholder="Rechercher un client, une référence…" value={search} onChange={event => setSearch(event.target.value)} /></label>
        <select aria-label="Filtrer par retour client" value={decisionFilter} onChange={event => setDecisionFilter(event.target.value)}><option value="all">Tous les retours client</option>{Object.entries(decisions).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
        <select aria-label="Trier les CV proposés" value={sort} onChange={event => setSort(event.target.value)}><option value="recent">CV récents par {byCandidate ? "candidat" : "client"}</option><option value="oldest">CV anciens par {byCandidate ? "candidat" : "client"}</option></select>
        <span className="text-xs text-slate-500">{visibleRows.length} présentation{visibleRows.length > 1 ? "s" : ""}</span>
        {clientFilter && <button type="button" className="cvRefresh" onClick={() => { setClientFilter(""); setSearch(""); setDecisionFilter("all"); }}>Tous les clients</button>}
      </div>
      <div className="cvDeliveryDateFilters" role="group" aria-label="Filtrer par date d’envoi">
        <span>Date d’envoi</span>
        <label>Du <input type="date" aria-label="Date de début des envois" value={dateFrom} onChange={event => { const value=event.target.value; setDateFrom(value); if(value && dateTo && value > dateTo) setDateTo(value); }} /></label>
        <label>Au <input type="date" aria-label="Date de fin des envois" min={dateFrom || undefined} value={dateTo} onChange={event => setDateTo(event.target.value)} /></label>
        {(dateFrom || dateTo) && <button type="button" className="cvRefresh" onClick={() => { setDateFrom(""); setDateTo(""); }}>Toutes les dates</button>}
      </div>
      </>}
      {clientLoading && <p role="status">Chargement de la fiche client…</p>}
      {!clientView && selectedClient && <Suspense fallback={<p role="status">Ouverture de la fiche client…</p>}>
        <ClientDetail key={selectedClient._id} client={selectedClient} setClient={setSelectedClient} clients={clients} setClients={setClients} originalClients={clients} />
      </Suspense>}
      {notice && (
        <p role="status" className="rounded bg-blue-50 p-2 mb-3">
          {notice}
        </p>
      )}
      {error && !edit && (
        <p role="alert" className="text-red-700 mb-3">
          {error}
        </p>
      )}
      {loading ? (
        <p>Chargement…</p>
      ) : !rows.length && !error ? (
        <p className="py-6 text-sm text-slate-500">Aucun candidat présenté.</p>
      ) : null}
      {!loading && rows.length > 0 && !visibleRows.length && <p className="py-6 text-sm text-slate-500">Aucune présentation ne correspond à ces filtres.</p>}
      <div className="cvDeliveryTableScroll">
        <table className="cvDeliveryTable" aria-label="CV proposés" data-responsive="custom" data-pagination="none">
          <thead><tr>
            <th>{clientView ? "Profil" : "Candidat"}</th><th>Client</th><th>Date d’envoi</th><th>Commande</th>
            {!clientView && <><th>Destinataire / Envoi</th><th>Présenté par</th></>}
            <th>Retour client</th><th>Actions</th>
          </tr></thead>
          {pagedGroups.map(group => <tbody key={group.key}>
            {!compact && <tr className="cvClientGroup"><th scope="rowgroup" colSpan={clientView ? 6 : 8}>
              <div><strong>{!byCandidate && !clientView && group.rows[0]?.clientId ? clientLabel(group.rows[0]) : group.name}</strong><span>{group.count} CV proposé{group.count > 1 ? "s" : ""}</span>{byCandidate && <span>{group.clients.size} client{group.clients.size > 1 ? "s" : ""}</span>}<span className="cvClientPending">{group.pending} retour{group.pending > 1 ? "s" : ""} en attente</span></div>
            </th></tr>}
            {group.rows.map(row => <tr key={row._id} id={`presentation-${row._id}`}>
            <td>
              {!clientView && row.candidateId ? <>
                <a className="cvCandidateLink" href={`/candidatsPage?fiche=${encodeURIComponent(row.candidateId)}`}>{row.candidateName || candidateNames[row.candidateId] || "Fiche candidat"}</a>
                <small className="text-slate-400">{row.reference}</small>
              </> : <strong>{row.reference}</strong>}
            </td>
            <td className="cvClientCell">{clientLabel(row)}</td>
            <td className="cvDateCell">{date(row.sentAt || row.createdAt)}</td>
            <td>{row.orderId || <span className="text-slate-400">Spontanée</span>}</td>
            {!clientView && <><td className="cvRecipientCell">{row.recipientEmail || "Non renseigné"}<small className={row.status === "failed" ? "text-red-700" : "text-slate-500"}>{{demo:"Démonstration — aucun email envoyé",sent:"Envoi pris en charge",failed:"Échec de l’envoi",sending:"Confirmation en attente"}[row.status] || row.status}</small></td><td>{row.senderName || "—"}</td></>}
            <td><b className={`cvStatus ${statusTone(row.clientDecision)}`}>{decisions[row.clientDecision] || "En attente"}</b>
            </td>
            <td>
            <div className="cvDeliveryActions">
              <Tooltip title="Voir le CV anonymisé">
              <button
                type="button"
                aria-label="Voir le CV anonymisé"
                className="text-blue-700 underline"
                onClick={() => view(row)}
              >
                <Eye size={17} aria-hidden="true" />
              </button>
              </Tooltip>
              {!clientView && <Tooltip title="Consulter l’email de présentation">
                <button type="button" aria-label="Consulter l’email de présentation" onClick={() => setEmailPreview(row)}><Mail size={17} aria-hidden="true" /></button>
              </Tooltip>}
              {!clientView && row.canRecordCandidate && <Tooltip title="Enregistrer le retour client">
                <button type="button" aria-label="Enregistrer le retour client" onClick={() => begin(row, "client-decision")}><MessageSquare size={17} aria-hidden="true" /></button>
              </Tooltip>}
              {!clientView && row.canRecordCandidate && row.clientDecision === "interview_requested" && <Tooltip title="Suivi de la demande d’entretien">
                <button type="button" aria-label="Suivi de la demande d’entretien" onClick={() => openInterviewFollowup(row)}><CalendarCheck size={17} aria-hidden="true" /></button>
              </Tooltip>}
              {row.canRecordCandidate && !clientView && (
                <>
                  <Tooltip title="Rattacher ou modifier la commande">
                  <button
                    type="button"
                    aria-label="Rattacher ou modifier la commande"
                    className="text-blue-700 underline"
                    onClick={() => begin(row, "order")}
                  >
                    <Link2 size={17} aria-hidden="true" />
                  </button>
                  </Tooltip>
                </>
              )}
              {clientView && row.canReview && (
                <Tooltip title="Répondre au profil">
                <button
                  type="button"
                  aria-label="Répondre au profil"
                  className="text-blue-700 underline"
                  onClick={() => begin(row, "client-decision")}
                >
                  <UserCheck size={17} aria-hidden="true" />
                </button>
                </Tooltip>
              )}
              {staff && (
                <Tooltip title="Habilitations client">
                <button
                  type="button"
                  aria-label="Habilitations client"
                  className="text-blue-700 underline"
                  onClick={() => manage(row)}
                >
                  <ShieldCheck size={17} aria-hidden="true" />
                </button>
                </Tooltip>
              )}
              <Tooltip title="Historique des réponses et commentaires">
                <button type="button" aria-label={`Historique du profil ${row.reference}`} onClick={() => setHistoryId(row._id)}><History size={17} aria-hidden="true" /></button>
              </Tooltip>
            </div>
              {clientView && !row.canReview && <small className="text-slate-500">Consultation uniquement</small>}
            </td>

          </tr>)}</tbody>)}
        </table>
      </div>
      <div className="cvDeliveryPagination">
        <label>Lignes par page <select aria-label="Lignes par page" value={pageSize} onChange={event => { setPageSize(Number(event.target.value)); setPage(1); }}>{[10,25,50,100].map(size => <option key={size} value={size}>{size}</option>)}</select></label>
        <span>{visibleRows.length ? (currentPage - 1) * pageSize + 1 : 0}–{Math.min(currentPage * pageSize, visibleRows.length)} sur {visibleRows.length}</span>
        <button type="button" disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)}>Précédent</button>
        <span>Page {currentPage} / {pageCount}</span>
        <button type="button" disabled={currentPage >= pageCount} onClick={() => setPage(currentPage + 1)}>Suivant</button>
      </div>
      <Modal open={!!historyRow} onCancel={() => setHistoryId(null)} footer={null} title={historyRow ? `Historique · ${historyRow.reference}` : "Historique"} width={620}>
        {historyRow && <div className="max-h-[60vh] overflow-y-auto py-2 text-sm text-slate-700">
          <p className="mb-4 text-slate-500">{historyRow.clientName}</p>
          {!historyRow.events?.length && <p>Aucun retour enregistré pour ce profil.</p>}
              <ol className="mt-2 space-y-2">
                {(historyRow.events || []).map((event, index) => (
                  <li key={index} className="border-l pl-3">
                    <p>
                      {date(event.at)} · {event.author} ·{" "}
                      {channels[event.channel] || "Suivi"}
                    </p>
                    <p>
                      {event.type === "client-decision"
                        ? `Client : ${decisions[event.status]}`
                        : event.type === "candidate-response"
                          ? `Candidat : ${responses[event.status]}`
                          : event.type === "interview-notification" ? "Notification de demande d’entretien envoyée" : `Commande : ${event.orderId || "aucune"}`}
                    </p>
                    {event.comment && (
                      <p className="whitespace-pre-wrap">{event.comment}</p>
                    )}
                    {event.interviewDetails && <p>Entretien le {event.interviewDetails.date.split('-').reverse().join('/')} à {event.interviewDetails.hour} · {event.interviewDetails.address}</p>}
                  </li>
                ))}
              </ol>
        </div>}
      </Modal>
      <Modal open={!!interviewFollowup} title="Suivi de la demande d’entretien" centered width={560} footer={null} closable={!busy} maskClosable={!busy} keyboard={!busy} onCancel={() => {if(!busy)setInterviewFollowup(null);}}>
        {interviewFollowup && <div className="cvReturnForm">
          <div className="cvReturnContext"><strong>{interviewFollowup.row.clientName}</strong><span>{interviewFollowup.row.candidateName || candidateNames[interviewFollowup.row.candidateId] || interviewFollowup.row.reference}</span></div>
          {interviewFollowup.interviewDetails && <div className="rounded-lg border border-slate-200 p-3 text-sm"><p>Le {interviewFollowup.interviewDetails.date.split('-').reverse().join('/')} à {interviewFollowup.interviewDetails.hour}</p><p>{interviewFollowup.interviewDetails.address}</p></div>}
          {interviewFollowup.loading ? <p role="status">Création et vérification du suivi…</p> : <>
            {interviewFollowup.taskCreated && <p className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800">La tâche « Entretien » est créée et liée aux fiches client et candidat. Elle est attribuée au freelance ayant présenté le CV.</p>}
            {interviewFollowup.notificationStatus === "sent" ? <p role="status">La notification a été envoyée au candidat.</p> : interviewFollowup.notificationStatus === "sending" ? <p role="status">L’envoi de la notification est en cours.</p> : interviewFollowup.pushAvailable ? <>
              <p>Souhaitez-vous envoyer une notification au candidat sur Mon intérim ?</p>
              <p className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600">{interviewFollowup.interviewDetails ? `Entretien le ${interviewFollowup.interviewDetails.date.split('-').reverse().join('/')} à ${interviewFollowup.interviewDetails.hour}. Adresse : ${interviewFollowup.interviewDetails.address}` : "Un client souhaite vous rencontrer. Votre interlocuteur JM Intérim vous contactera pour organiser l’entretien."}</p>
            </> : interviewFollowup.taskCreated && <p className="text-sm text-slate-500">Aucune notification push disponible pour ce candidat sur Mon intérim. La tâche reste créée.</p>}
            {interviewFollowup.error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{interviewFollowup.error}</p>}
            {interviewFollowup.taskCreated && <button type="button" disabled={busy} className="text-left text-sm text-blue-700 underline" onClick={() => {const row={...interviewFollowup.row,interviewDetails:interviewFollowup.interviewDetails || interviewFollowup.row.interviewDetails};setInterviewFollowup(null);begin(row,"client-decision");}}>Modifier la date, l’heure ou l’adresse</button>}
            <div className="cvReturnFooter">
              {interviewFollowup.pushAvailable && !["sent","sending"].includes(interviewFollowup.notificationStatus) && <button type="button" disabled={busy} onClick={notifyInterview} className="bg-blue-700 text-white">{busy ? "Envoi…" : "Envoyer la notification"}</button>}
              {!interviewFollowup.taskCreated && <button type="button" onClick={() => openInterviewFollowup(interviewFollowup.row)}>Réessayer la création du suivi</button>}
              <button type="button" disabled={busy} onClick={() => setInterviewFollowup(null)}>{interviewFollowup.pushAvailable && !["sent","sending"].includes(interviewFollowup.notificationStatus) ? "Ne pas notifier" : "Fermer"}</button>
            </div>
          </>}
        </div>}
      </Modal>
      <Modal open={!!emailPreview && !clientView} title="Email de présentation" centered width={720} footer={null} onCancel={() => setEmailPreview(null)}>
        {emailPreview && !clientView && <div className="cvReturnForm">
          <div className="cvReturnContext">
            <strong>{emailPreview.subject || "Objet non renseigné"}</strong>
            <span>À : {emailPreview.recipientEmail || "Non renseigné"}</span>
            <span>Présenté par : {emailPreview.senderName || "Non renseigné"}</span>
            <span>Date : {date(emailPreview.sentAt || emailPreview.createdAt)}</span>
            <span>Statut : {{sent:"Envoi pris en charge",failed:"Échec de l’envoi",sending:"Confirmation en attente",demo:"Démonstration — aucun email envoyé"}[emailPreview.status] || emailPreview.status}</span>
          </div>
          <div className="max-h-[45vh] overflow-y-auto whitespace-pre-wrap rounded-lg border border-slate-200 p-4 text-sm leading-7">{emailPreview.message || "Le message n’est pas disponible pour cet envoi."}</div>
          <p className="text-xs text-slate-500">Message enregistré lors de l’envoi. La signature et le lien vers l’espace client sont ajoutés automatiquement à l’email.</p>
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-slate-50 p-3 text-sm"><span>Pièce jointe : {emailPreview.filename || "CV anonymisé"}</span><button type="button" className="text-blue-700 underline" onClick={() => view(emailPreview)}>Voir le CV joint</button></div>
        </div>}
      </Modal>
      <Modal open={!!edit} onCancel={() => { if (!busy) setEdit(null); }} footer={null} centered width={580} closable={!busy} maskClosable={!busy} keyboard={!busy}
        title={edit?.type === "order" ? "Rattacher une commande" : "Enregistrer le retour client"}>
      {edit && (
        <form
          onSubmit={save}
          className="cvReturnForm"
        >
          <div className="cvReturnContext"><strong>{edit.row.clientName}</strong><span>{edit.row.candidateName || candidateNames[edit.row.candidateId] || edit.row.reference}</span></div>
          {error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
          {edit.type === "order" ? (
            <label className="block">
              Commande
              <select
                aria-label="Commande associée"
                className="block border rounded p-2 w-full"
                value={selectedOrder}
                onChange={(e) => setSelectedOrder(e.target.value)}
              >
                <option value="">Sans commande</option>
                {orders.map((o) => (
                  <option key={o._id} value={o._id}>
                    {o.orderNumber || o._id} · {o.qualification || ""}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <>
              <label className="block">
                Réponse
                <select
                  aria-label="Réponse"
                  className="block border rounded p-2 w-full"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  {Object.entries(
                    edit.type === "client-decision" ? decisions : responses,
                  ).map(([value, text]) => (
                    <option key={value} value={value}>
                      {text}
                    </option>
                  ))}
                </select>
              </label>
              {(edit.type === "candidate-response" || (edit.type === "client-decision" && !clientView)) && (
                <label className="block">
                  Canal
                  <select
                    aria-label="Canal"
                    className="block border rounded p-2 w-full"
                    value={channel}
                    onChange={(e) => setChannel(e.target.value)}
                  >
                    {Object.entries(channels)
                      .filter(([key]) => key !== "client-space")
                      .map(([value, text]) => (
                        <option key={value} value={value}>
                          {text}
                        </option>
                      ))}
                  </select>
                </label>
              )}
              {edit.type === "client-decision" && status === "interview_requested" && <div className="cvInterviewFields">
                <label>Date de l’entretien<input type="date" aria-label="Date de l’entretien" required value={interviewDetails.date} onChange={event => setInterviewDetails(current=>({...current,date:event.target.value}))} /></label>
                <label>Heure de l’entretien<input type="time" aria-label="Heure de l’entretien" required value={interviewDetails.hour} onChange={event => setInterviewDetails(current=>({...current,hour:event.target.value}))} /></label>
                <label className="cvInterviewAddress">Adresse de l’entretien<input type="text" aria-label="Adresse de l’entretien" required maxLength={1000} placeholder="Adresse complète du rendez-vous" value={interviewDetails.address} onChange={event => setInterviewDetails(current=>({...current,address:event.target.value}))} /></label>
              </div>}
              <label className="block">
                {status === "declined" ? "Motif du refus" : "Commentaire"}
                <textarea
                  aria-label="Commentaire du retour"
                  className="block border rounded p-2 w-full"
                  required={
                    edit.type === "candidate-response" || (edit.type === "client-decision" && !clientView) || status === "declined"
                  }
                  maxLength={2000}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
              </label>
              {edit.type === "candidate-response" && (
                <p className="text-xs text-slate-500">
                  Ce commentaire reste interne à l’agence et au freelance.
                </p>
              )}
            </>
          )}
          <div className="cvReturnFooter">
          <button
            disabled={busy}
            type="submit"
            className="rounded bg-blue-700 px-4 py-2 text-white"
          >
            {busy ? "Enregistrement…" : "Enregistrer le retour"}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => setEdit(null)}
            className="ml-4"
          >
            Annuler
          </button>
          </div>
        </form>
      )}
      </Modal>
      {reviewers && (
        <section className="mt-4 border rounded p-4">
          <h4 className="font-semibold">
            Comptes autorisés à répondre aux candidats
          </h4>
          {!reviewers.rows.length && (
            <p>Aucun compte client actif rattaché à cette entreprise.</p>
          )}
          {reviewers.rows.map((user) => (
            <label className="block my-2" key={user._id}>
              <input
                type="checkbox"
                checked={user.canReview}
                disabled={busy}
                onChange={(e) => permission(user, e.target.checked)}
              />{" "}
              {user.name} · {user.email}
            </label>
          ))}
          <button
            type="button"
            onClick={() => setReviewers(null)}
            className="underline"
          >
            Fermer
          </button>
        </section>
      )}
      <Modal
        open={!!preview}
        title={`CV anonymisé${previewReference ? ` · ${previewReference}` : ""}`}
        onCancel={() => setPreview(null)}
        width="92vw"
        centered
        destroyOnClose
        footer={preview ? <a href={preview} target="_blank" rel="noopener noreferrer" className="inline-flex rounded-lg border border-slate-200 px-4 py-2 text-sm text-blue-700">Ouvrir dans un nouvel onglet</a> : null}
        styles={{ body: { padding: 0, overflow: "hidden", background: "#f1f5f9", borderRadius: 8 } }}
      >
        {preview && (
          <iframe
            title="Version du CV envoyé"
            src={`${preview}#view=FitH`}
            className="block w-full border-0"
            style={{ height: "75vh" }}
          />
        )}
      </Modal>
    </section>
  );
}
